import { Body, Caption, CustomLink, Tag, styled } from '@teko/ds-desktop';
import { EnhancedImage } from '@teko/teko-ecommerce';
import { LinkInterface } from '@teko/types/ds';
import {
  ComboImage,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Gift,
  getComboImageUrl,
  getComboName,
} from '@teko/ui-cart';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useState } from 'react';
import { AvailableLeft } from '../shared';

interface ProductInfoProps {
  product: ProductProps;
  className?: string;
}

interface ComboProps {
  imageUrl: string;
  name: string;
}

interface ProductProps {
  displaySku?: string;
  imageUrl: string;
  name: string;
  displayName?: string;
  quantity: number;
  price: number;
  combos?: ComboProps[];
  link?: LinkInterface;
  childItems?: ChildProductProps[];
  tags?: string[];
  available: number;
  displayTotalAvailable: string;
  attributeDescription?: string;
  uomName?: string;
}

interface ChildProductProps {
  sku?: string;
  imageUrl: string;
  name: string;
  quantity: number;
  price: number;
}

interface StyledActionProps {
  mgl?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const Right = styled.div`
  flex: 1;
`;

const Action = styled.span<StyledActionProps>(
  ({ mgl = true }: StyledActionProps) => `
      font-size: 14px;
      color: #047fff;
      margin-left: ${mgl ? '1.5rem' : 0};
      cursor: pointer;
      display: inline-block;
      width: max-content;
      flex-shrink: 0;
    `,
);

const ComboList = styled.ul`
  padding: 0.25rem 0.75rem;
  list-style-type: none;
  border-radius: 0.5rem;
  box-shadow: 0 2px 0.5rem 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  background-color: white;
  margin: 0;
  width: max-content;
  max-width: 25rem;
`;

const StyledDropdown = styled(Dropdown)`
  width: max-content;
`;

const popperConfig = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 10],
      },
    },
  ],
};

const StyledGift = styled(Gift)`
  margin: 0.5rem 0;
`;

const Image = styled(EnhancedImage)`
  border: 1px solid #e4e5f0;
  border-radius: 4px;
`;

export const ProductInfo: ({
  product: {
    displaySku,
    imageUrl,
    name,
    displayName,
    combos,
    link,
    childItems,
    tags,
    available,
    displayTotalAvailable,
    attributeDescription,
    uomName,
  },
  className,
}: ProductInfoProps) => JSX.Element = ({
  product: {
    displaySku,
    imageUrl,
    name,
    displayName,
    combos = [],
    link,
    childItems = [],
    tags = [],
    displayTotalAvailable = '',
    attributeDescription,
    uomName,
  },
  className,
}: ProductInfoProps) => {
  const [comboOpen, setComboOpen] = useState(false);
  const isCombo = childItems.length > 0;
  const toggleCombo = (): void => {
    setComboOpen((prev) => !prev);
  };
  const { isDisplayUom } = useTenantConfig();
  const image = isCombo ? (
    <ComboImage images={getComboImageUrl(childItems)} />
  ) : (
    <Image height={80} width={80} src={imageUrl} alt="product" />
  );
  const productName = isCombo ? getComboName(childItems) : displayName || name;

  return (
    <Wrapper className={className}>
      {!!link ? <CustomLink {...link}>{image}</CustomLink> : <>{image}</>}
      <Right>
        {!!link ? (
          <CustomLink {...link}>
            <Body
              hoverColor="primary500"
              level={1}
              color="textPrimary"
              weight="regular"
              line={2}
            >
              {tags && tags.length > 0 && (
                <>
                  <Tag type="secondary" variant="branding" content={tags[0]} />
                  &nbsp;
                </>
              )}
              {productName}
            </Body>
          </CustomLink>
        ) : (
          <Body
            hoverColor="primary500"
            level={1}
            color="textPrimary"
            weight="regular"
            line={2}
          >
            {tags && tags.length > 0 && (
              <>
                <Tag type="secondary" variant="branding" content={tags[0]} />
                &nbsp;
              </>
            )}
            {productName}
          </Body>
        )}

        <>
          {displaySku && (
            <Caption level={1} color="textSecondary">
              SKU: {displaySku}
            </Caption>
          )}
          {attributeDescription && (
            <Caption level={1} color="textSecondary">
              {attributeDescription}
            </Caption>
          )}
          {isDisplayUom && !!uomName ? (
            <Caption level={1} color="textSecondary">
              {t('Đơn vị tính:')} {uomName}
            </Caption>
          ) : null}
          {displayTotalAvailable !== '' && (
            <AvailableLeft
              displayTotalAvailable={displayTotalAvailable}
              isMobile={false}
            />
          )}
        </>

        {!!combos.length && (
          <StyledDropdown
            isOpen={comboOpen}
            toggle={toggleCombo}
            toggleType="hover"
          >
            <DropdownToggle toggleType="hover">
              <Action mgl={false}>{t('Xem combo')}</Action>
            </DropdownToggle>
            <DropdownMenu popperConfig={popperConfig} origin="left top">
              <ComboList>
                {combos.map(({ imageUrl, name }, index) => (
                  <StyledGift
                    key={index}
                    image={{
                      src: imageUrl,
                    }}
                    text={name}
                  />
                ))}
              </ComboList>
            </DropdownMenu>
          </StyledDropdown>
        )}
      </Right>
    </Wrapper>
  );
};
